/**
 * TV WALL — Hybrid layout with 4 slides + theme + settings
 *
 * Slides (configurable via settings):
 *   1. Overall KPI grid (with gap row + daily-needed callout per card)
 *   2. Channel Performance (replaces old Spotlight)
 *   3. Chart Visual (bar chart % progress)
 *   4. Target Tracker (table — target / actual / gap / daily needed)
 *
 * Persistent sidebar: Month progress + Top channels leaderboard
 * Persistent header: Logo · Live · Slide name · Days remaining · Nav controls · Settings · Theme toggle · Clock
 *
 * Settings (localStorage persisted): enabled slides, slide duration, theme.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useTvWallData, fmt, statusColor } from './useTvWallData';
import {
  Crown, Calendar, Activity, ChevronLeft, ChevronRight, Pause, Play,
  Wallet, AlertTriangle, Settings, Sun, Moon, BarChart3, Table2, Layers,
  TrendingUp, TrendingDown, Trophy, Target, ArrowUp, ArrowRight,
  Building2, X, Zap, Award, LineChart as LineChartIcon,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid, Legend } from 'recharts';
import axios from 'axios';

const DEFAULT_SETTINGS = {
  slides: { overall: true, channels: true, chart: true, tracker: true, pace: true, comparison: true },
  duration_sec: 30,
  theme: 'dark',
  pace_metrics: ['REV', 'LEAD', 'CLS', 'LOT'], // which metrics to show in Pace slide
  comparison_metrics: ['REV', 'LEAD', 'CLS', 'LOT', 'SUB', 'ACA'], // metrics for MoM comparison slide
};

const SLIDE_META = [
  { key: 'overall',    label: 'Overall KPI',      icon: Layers },
  { key: 'channels',   label: 'Channels',         icon: Building2 },
  { key: 'chart',      label: 'Chart Visual',     icon: BarChart3 },
  { key: 'tracker',    label: 'Target Tracker',   icon: Table2 },
  { key: 'pace',       label: 'MTD Pace Trend',   icon: LineChartIcon },
  { key: 'comparison', label: 'MoM Comparison',   icon: TrendingUp },
];

function loadSettings() {
  try {
    const raw = localStorage.getItem('tv_wall_settings');
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_SETTINGS, ...parsed, slides: { ...DEFAULT_SETTINGS.slides, ...(parsed.slides || {}) } };
  } catch { return DEFAULT_SETTINGS; }
}

function saveSettings(s) {
  try { localStorage.setItem('tv_wall_settings', JSON.stringify(s)); } catch { /* noop */ }
}

const THEME = {
  dark: {
    bg: '#0a0a0a',
    surface: 'hsl(var(--surface))',
    surfaceElev: 'hsl(var(--surface-elevated))',
    border: 'hsl(var(--border))',
    text: '#ffffff',
    textDim: '#9CA3AF',
    textMute: '#6B7280',
  },
  light: {
    bg: '#F8FAFC',
    surface: '#FFFFFF',
    surfaceElev: '#F1F5F9',
    border: '#E2E8F0',
    text: '#0F172A',
    textDim: '#475569',
    textMute: '#94A3B8',
  },
};

/* ------------------------- Card primitives ------------------------- */

function Tile({ m, t }) {
  const sc = statusColor(m.pct);
  return (
    <div className="relative rounded-xl p-2.5 overflow-hidden border" style={{ background: t.surface, borderColor: t.border }}>
      <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${m.color}, transparent)` }} />
      <div className="absolute -top-6 -right-6 w-16 h-16 rounded-full blur-2xl opacity-20" style={{ backgroundColor: m.color }} />
      <div className="relative">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium mb-1" style={{ color: t.textDim }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.color }} />
          {m.label}
        </div>
        <div className="flex items-baseline justify-between gap-1">
          <p className="text-3xl font-bold tabular-nums leading-tight" style={{ color: t.text }}>
            {m.prefix && <span className="text-base mr-0.5" style={{ color: t.textDim }}>{m.prefix}</span>}
            {fmt(m.value, { compact: m.value >= 100000, decimals: m.value < 100 ? 2 : 0 })}
            {m.suffix && <span className="text-base" style={{ color: t.textDim }}>{m.suffix}</span>}
          </p>
          <span className="text-base tabular-nums font-bold" style={{ color: sc }}>{m.target > 0 ? `${Math.min(m.pct, 100).toFixed(0)}%` : '—'}</span>
        </div>
        <div className="h-1 mt-1.5 rounded-full overflow-hidden" style={{ background: t.surfaceElev }}>
          <div className="h-full transition-all duration-700" style={{ width: `${Math.min(m.pct, 100)}%`, background: sc }} />
        </div>
        {m.target > 0 ? (
          <div className="flex items-center justify-between mt-1.5 text-[11px]" style={{ color: t.textMute }}>
            <span>of {m.prefix || ''}{fmt(m.target, { compact: true })}</span>
            <span className="flex items-center gap-0.5" style={{ color: m.gap > 0 ? sc : '#10B981' }}>
              {m.gap > 0
                ? <><ArrowUp size={10} />gap {m.prefix || ''}{fmt(m.gap, { compact: true })}</>
                : <><Trophy size={10} />achieved</>}
            </span>
          </div>
        ) : (
          <p className="text-[11px] italic mt-1.5" style={{ color: t.textMute }}>No target</p>
        )}
      </div>
    </div>
  );
}

/* ------------------------- Slide 1: OVERALL ------------------------- */

function OverallView({ data, metrics, t }) {
  const totalRev = metrics.find(m => m.code === 'REV')?.value || 0;
  const totalRevTarget = metrics.find(m => m.code === 'REV')?.target || 0;
  const revGap = Math.max(0, totalRevTarget - totalRev);
  const revPct = totalRevTarget > 0 ? (totalRev / totalRevTarget * 100) : 0;
  const onTrack = data.summary?.on_track || 0;
  const atRisk = data.summary?.at_risk || 0;
  const offTrack = data.summary?.off_track || 0;
  const linkedChannels = data.summary?.linked_channels || 0;

  return (
    <div className="flex flex-col gap-2 min-h-0 flex-1">
      {/* Revenue + Health hero */}
      <div className="grid grid-cols-2 gap-2">
        <div className="relative rounded-xl p-4 overflow-hidden border" style={{ background: `linear-gradient(135deg, ${t.theme === 'light' ? '#10B98115' : '#10B98115'}, ${t.surface})`, borderColor: '#10B98140' }}>
          <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl bg-[#10B981]/25" />
          <div className="relative">
            <p className="text-[13px] uppercase tracking-[0.15em] mb-1 flex items-center gap-1.5" style={{ color: t.textDim }}>
              <Wallet size={14} className="text-[#10B981]" /> Combined Revenue · {data.period}
            </p>
            <p className="text-[64px] font-bold tabular-nums tracking-tight leading-none" style={{ color: t.text }}>
              <span className="text-3xl text-[#10B981] mr-1">RM</span>
              {fmt(totalRev, { decimals: 0 })}
            </p>
            <div className="mt-2 flex items-center gap-3 text-sm" style={{ color: t.textDim }}>
              <span>Target RM {fmt(totalRevTarget, { decimals: 0 })}</span>
              <span className="text-[#10B981] font-bold tabular-nums">{revPct.toFixed(1)}%</span>
            </div>
            <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: t.surfaceElev }}>
              <div className="h-full bg-gradient-to-r from-[#10B981] to-[#3B82F6]" style={{ width: `${Math.min(revPct, 100)}%` }} />
            </div>
            <div className="mt-2 flex items-center gap-1.5 text-sm" style={{ color: revGap > 0 ? '#F59E0B' : '#10B981' }}>
              {revGap > 0 ? <><ArrowUp size={14} /><span>Gap RM {fmt(revGap, { decimals: 0 })} to hit target</span></> :
               <><Trophy size={14} /><span>Target achieved!</span></>}
            </div>
          </div>
        </div>
        <div className="relative rounded-xl p-4 overflow-hidden border" style={{ background: `linear-gradient(135deg, #3B82F615, ${t.surface})`, borderColor: '#3B82F640' }}>
          <p className="text-[13px] uppercase tracking-[0.15em] mb-2 flex items-center gap-1.5" style={{ color: t.textDim }}>
            <Activity size={14} className="text-[#3B82F6]" /> Channel Health · {linkedChannels} active
          </p>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <div className="text-center rounded-lg py-3 border" style={{ background: t.surface + '99', borderColor: '#10B98130' }}>
              <p className="text-5xl font-bold text-[#10B981] tabular-nums leading-none">{onTrack}</p>
              <p className="text-[11px] mt-1.5 uppercase" style={{ color: t.textMute }}>On Track</p>
            </div>
            <div className="text-center rounded-lg py-3 border" style={{ background: t.surface + '99', borderColor: '#F59E0B30' }}>
              <p className="text-5xl font-bold text-[#F59E0B] tabular-nums leading-none">{atRisk}</p>
              <p className="text-[11px] mt-1.5 uppercase" style={{ color: t.textMute }}>At Risk</p>
            </div>
            <div className="text-center rounded-lg py-3 border" style={{ background: t.surface + '99', borderColor: '#E6394630' }}>
              <p className="text-5xl font-bold text-[#E63946] tabular-nums leading-none">{offTrack}</p>
              <p className="text-[11px] mt-1.5 uppercase" style={{ color: t.textMute }}>Off Track</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI grid 4×4 (16 metrics) */}
      <div className="grid grid-cols-4 gap-2 flex-1 min-h-0" style={{ gridAutoRows: 'minmax(0, 1fr)' }}>
        {metrics.slice(0, 16).map(m => <Tile key={m.code} m={m} t={t} />)}
      </div>
    </div>
  );
}

/* ------------------------- Slide 2: CHANNELS ------------------------- */

function ChannelsView({ channels, t }) {
  return (
    <div className="flex flex-col gap-2 min-h-0 flex-1">
      <div className="flex items-center gap-2 mb-2">
        <Building2 size={20} className="text-[#3B82F6]" />
        <h2 className="text-2xl font-bold tracking-tight" style={{ color: t.text }}>Performance by Channel</h2>
        <p className="text-sm ml-2" style={{ color: t.textMute }}>· ranked by overall achievement</p>
      </div>
      <div className="grid grid-cols-2 gap-2 flex-1 min-h-0 content-start" style={{ gridAutoRows: 'minmax(0, 1fr)' }}>
        {channels.slice(0, 8).map((c, i) => {
          const sc = statusColor(c.overall.overall_pct);
          const initials = (c.channel_name || '?').split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
          const avatarUrl = c.brand_logo_url;
          const isFirst = i === 0;
          return (
            <div key={c.ib_id} className="relative rounded-xl p-3 overflow-hidden border" style={{
              background: isFirst ? `linear-gradient(135deg, #F59E0B15, ${t.surface})` : t.surface,
              borderColor: isFirst ? '#F59E0B50' : t.border,
            }}>
              {isFirst && (
                <div className="absolute top-2 right-2 px-2 py-0.5 bg-gradient-to-r from-[#F59E0B] to-[#E63946] text-white text-[8px] font-bold rounded-full flex items-center gap-1">
                  <Crown size={9} />LEADER
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="text-2xl font-bold tabular-nums w-10 text-center" style={{ color: isFirst ? '#F59E0B' : t.textMute }}>
                  {i === 0 ? <Trophy size={24} className="mx-auto text-[#F59E0B]" /> :
                   i === 1 ? <Award size={22} className="mx-auto text-[#9CA3AF]" /> :
                   i === 2 ? <Award size={22} className="mx-auto text-[#CD7F32]" /> :
                   <span className="text-lg font-bold" style={{ color: t.textMute }}>#{i+1}</span>}
                </div>
                <div className="w-12 h-12 rounded-lg overflow-hidden ring-1 flex-shrink-0" style={{ ringColor: t.border, background: `linear-gradient(135deg, ${sc}22, ${sc}11)` }}>
                  {avatarUrl
                    ? <img src={avatarUrl} alt={c.channel_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    : <div className="w-full h-full grid place-content-center font-bold text-white text-sm" style={{ background: `linear-gradient(135deg, ${sc}, ${sc}80)` }}>{initials}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold truncate" style={{ color: t.text }}>{c.channel_name}</p>
                  <p className="text-xs" style={{ color: t.textMute }}>{c.staff_count} staff · {c.overall.templates_count} metrics</p>
                </div>
                <div className="text-right">
                  <p className="text-4xl font-bold tabular-nums leading-none" style={{ color: sc }}>
                    {Math.min(c.overall.overall_pct, 100).toFixed(1)}<span className="text-lg">%</span>
                  </p>
                </div>
              </div>
              <div className="h-1.5 mt-2 rounded-full overflow-hidden" style={{ background: t.surfaceElev }}>
                <div className="h-full transition-all" style={{ width: `${Math.min(c.overall.overall_pct, 100)}%`, background: sc }} />
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs">
                <span className="flex items-center gap-0.5 text-[#10B981]"><span className="w-2 h-2 rounded-full bg-[#10B981]"/>{c.overall.on_track_count} on track</span>
                <span className="flex items-center gap-0.5 text-[#F59E0B]"><span className="w-2 h-2 rounded-full bg-[#F59E0B]"/>{c.overall.at_risk_count} at risk</span>
                <span className="flex items-center gap-0.5 text-[#E63946]"><span className="w-2 h-2 rounded-full bg-[#E63946]"/>{c.overall.off_track_count} off track</span>
              </div>
            </div>
          );
        })}
        {channels.length === 0 && (
          <div className="col-span-2 grid place-content-center py-12 text-center text-sm" style={{ color: t.textMute }}>
            No active channels yet
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------- Slide 3: CHART VISUAL ------------------------- */

function ChartView({ metrics, t }) {
  // Show top 10 metrics by absolute target as horizontal bar chart
  const visible = metrics.filter(m => m.target > 0).slice(0, 12);
  return (
    <div className="flex flex-col gap-2 min-h-0 flex-1">
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 size={20} className="text-[#8B5CF6]" />
        <h2 className="text-2xl font-bold tracking-tight" style={{ color: t.text }}>Achievement Progress</h2>
        <p className="text-sm ml-2" style={{ color: t.textMute }}>· bar chart by metric · sorted by target sequence</p>
      </div>
      <div className="rounded-xl p-4 flex-1 min-h-0 border overflow-y-auto" style={{ background: t.surface, borderColor: t.border }}>
        <div className="space-y-2.5">
          {visible.map(m => {
            const sc = statusColor(m.pct);
            const pctClamp = Math.min(m.pct, 100);
            return (
              <div key={m.code} className="grid grid-cols-[180px_1fr_auto] items-center gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: m.color }} />
                  <span className="text-sm font-medium truncate" style={{ color: t.text }}>{m.label}</span>
                </div>
                <div className="relative h-9 rounded-md overflow-hidden border" style={{ background: t.surfaceElev, borderColor: t.border }}>
                  <div className="absolute inset-y-0 left-0 rounded-md transition-all duration-700"
                       style={{ width: `${pctClamp}%`, background: `linear-gradient(90deg, ${sc}, ${sc}cc)` }} />
                  <div className="relative h-full flex items-center justify-between px-3 text-sm font-bold">
                    <span className="text-white drop-shadow tabular-nums">
                      {m.prefix || ''}{fmt(m.value, { compact: m.value >= 10000, decimals: m.value < 100 ? 2 : 0 })}{m.suffix || ''}
                    </span>
                    <span className="tabular-nums" style={{ color: t.text }}>
                      / {m.prefix || ''}{fmt(m.target, { compact: m.target >= 10000 })}{m.suffix || ''}
                    </span>
                  </div>
                </div>
                <div className="text-right min-w-[72px]">
                  <p className="text-lg font-bold tabular-nums" style={{ color: sc }}>{Math.min(m.pct, 100).toFixed(0)}%</p>
                  {m.gap > 0 && <p className="text-[11px] tabular-nums" style={{ color: t.textMute }}>−{fmt(m.gap, { compact: true })}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ------------------------- Slide 4: TARGET TRACKER ------------------------- */

function TrackerView({ metrics, weekdaysRemaining, t }) {
  const visible = metrics.filter(m => m.target > 0).slice(0, 14);
  return (
    <div className="flex flex-col gap-2 min-h-0 flex-1">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Table2 size={20} className="text-[#F59E0B]" />
          <h2 className="text-2xl font-bold tracking-tight" style={{ color: t.text }}>Daily Target Tracker</h2>
          <p className="text-sm ml-2" style={{ color: t.textMute }}>· What we need per weekday to catch up · {weekdaysRemaining} weekdays remaining</p>
        </div>
      </div>
      <div className="rounded-xl flex-1 min-h-0 border overflow-hidden" style={{ background: t.surface, borderColor: t.border }}>
        <div className="overflow-y-auto h-full">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10" style={{ background: t.surfaceElev }}>
              <tr className="text-left uppercase tracking-wider text-[11px]" style={{ color: t.textMute }}>
                <th className="px-4 py-3">Metric</th>
                <th className="px-2 py-3 text-right">Target</th>
                <th className="px-2 py-3 text-right">Actual</th>
                <th className="px-2 py-3 text-right">Gap</th>
                <th className="px-2 py-3 text-right">Daily Pace</th>
                <th className="px-2 py-3 text-right">Needed/Day</th>
                <th className="px-2 py-3 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(m => {
                const sc = statusColor(m.pct);
                const behind = m.daily_needed > m.daily_original;
                return (
                  <tr key={m.code} className="border-t" style={{ borderColor: t.border }}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                        <span className="font-semibold text-base" style={{ color: t.text }}>{m.label}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2.5 text-right text-base tabular-nums" style={{ color: t.textDim }}>{m.prefix || ''}{fmt(m.target, { compact: true })}{m.suffix || ''}</td>
                    <td className="px-2 py-2.5 text-right text-base tabular-nums font-bold" style={{ color: t.text }}>{m.prefix || ''}{fmt(m.value, { compact: true })}{m.suffix || ''}</td>
                    <td className="px-2 py-2.5 text-right text-base tabular-nums" style={{ color: m.gap > 0 ? '#F59E0B' : '#10B981' }}>{m.prefix || ''}{fmt(m.gap, { compact: true })}{m.suffix || ''}</td>
                    <td className="px-2 py-2.5 text-right text-base tabular-nums" style={{ color: t.textMute }}>{m.prefix || ''}{fmt(m.daily_original, { compact: true })}</td>
                    <td className="px-2 py-2.5 text-right text-base tabular-nums font-bold flex items-center justify-end gap-1" style={{ color: behind ? '#E63946' : '#10B981' }}>
                      {behind && <ArrowUp size={12} />}
                      {m.prefix || ''}{fmt(m.daily_needed, { compact: true })}{m.suffix || ''}
                    </td>
                    <td className="px-2 py-2.5 text-right text-base tabular-nums font-bold" style={{ color: sc }}>{m.pct.toFixed(0)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ------------------------- Slide 5: MTD PACE ------------------------- */

function PaceChart({ metric, t }) {
  const data = metric.series || [];
  const sc = metric.diff_now >= 0 ? '#10B981' : '#E63946';
  const finalActual = (data.filter(p => p.cum_actual !== null).slice(-1)[0]?.cum_actual) || 0;
  const finalTarget = (data.slice(-1)[0]?.cum_target) || 0;
  const pctOfTarget = finalTarget > 0 ? (finalActual / finalTarget * 100) : 0;
  const projectedFinal = metric.projected_final;
  const projectedPct = metric.projected_pct;
  const projColor = projectedPct == null ? '#6B7280'
    : projectedPct >= 100 ? '#10B981'
    : projectedPct >= 80 ? '#F59E0B'
    : '#E63946';
  const trend = metric.trend;
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : ArrowRight;
  const trendColor = trend === 'up' ? '#10B981' : trend === 'down' ? '#E63946' : '#F59E0B';
  return (
    <div className="rounded-xl p-3 border overflow-hidden" style={{ background: t.surface, borderColor: t.border }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: metric.color }} />
          <p className="text-xs font-semibold truncate" style={{ color: t.text }}>{metric.name}</p>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="font-bold tabular-nums" style={{ color: sc }}>
            {metric.diff_now >= 0 ? '+' : ''}{metric.prefix || ''}{Math.round(metric.diff_now).toLocaleString()}{metric.suffix || ''}
          </span>
          <TrendIcon size={11} style={{ color: trendColor }} />
        </div>
      </div>
      <div style={{ height: 125 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 2, right: 4, bottom: 0, left: 4 }}>
            <CartesianGrid stroke={t.border} strokeDasharray="2 4" opacity={0.4} />
            <XAxis dataKey="day" tick={{ fontSize: 9, fill: t.textMute }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: t.textMute }} width={32} axisLine={false} tickLine={false}
              tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : Math.round(v).toLocaleString()} />
            <Tooltip
              contentStyle={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 11 }}
              labelStyle={{ color: t.text, fontWeight: 600 }}
              itemStyle={{ color: t.text }}
              formatter={(v, n) => {
                const label = n === 'cum_actual' ? 'Actual' : n === 'cum_target' ? 'Target' : 'Projected';
                return [`${metric.prefix || ''}${Number(v || 0).toLocaleString()}${metric.suffix || ''}`, label];
              }}
              labelFormatter={(l) => `Day ${l}`}
            />
            <Line type="monotone" dataKey="cum_target" stroke="#6B7280" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
            <Line type="monotone" dataKey="cum_projected" stroke={projColor} strokeWidth={1.5} strokeDasharray="2 3" dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="cum_actual" stroke={sc} strokeWidth={2.5} dot={false}
              isAnimationActive={false} connectNulls={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[9px]" style={{ color: t.textMute }}>
        <span><span style={{ color: sc }}>━</span> {metric.prefix || ''}{Math.round(finalActual).toLocaleString()}{metric.suffix || ''}</span>
        <span><span className="text-[#6B7280]">┄</span> {metric.prefix || ''}{Math.round(finalTarget).toLocaleString()}{metric.suffix || ''}</span>
        {projectedFinal != null && (
          <span style={{ color: projColor }} className="font-bold tabular-nums">
            <span>┈</span> proj {metric.prefix || ''}{Math.round(projectedFinal).toLocaleString()}{metric.suffix || ''} ({projectedPct.toFixed(0)}%)
          </span>
        )}
        <span className="font-bold" style={{ color: pctOfTarget >= 100 ? '#10B981' : pctOfTarget >= 80 ? '#F59E0B' : '#E63946' }}>{pctOfTarget.toFixed(0)}% now</span>
      </div>
    </div>
  );
}

function PaceView({ paceData, paceMetrics, metricsMeta, t, period }) {
  if (!paceData) {
    return <div className="grid place-content-center flex-1 text-sm" style={{ color: t.textMute }}>Loading pace data...</div>;
  }
  // Enrich each metric with trend + diff
  const enriched = paceMetrics.map(code => {
    const m = paceData.metrics?.[code];
    if (!m) return null;
    const meta = metricsMeta[code] || {};
    const visiblePoints = m.series.filter(p => p.cum_actual !== null);
    const last = visiblePoints[visiblePoints.length - 1];
    const prev = visiblePoints[visiblePoints.length - 3] || visiblePoints[0]; // 3-day window
    const diff_now = last ? (last.cum_actual - last.cum_target) : 0;
    let trend = 'flat';
    if (prev && last) {
      const recentDiff = (last.cum_actual - last.cum_target) - (prev.cum_actual - prev.cum_target);
      if (recentDiff > 0.01) trend = 'up';
      else if (recentDiff < -0.01) trend = 'down';
    }
    return { ...m, color: meta.color || '#10B981', prefix: meta.prefix, suffix: meta.suffix, diff_now, trend };
  }).filter(Boolean);

  return (
    <div className="flex flex-col gap-2 min-h-0 flex-1">
      <div className="flex items-center gap-2 mb-2">
        <LineChartIcon size={20} className="text-[#3B82F6]" />
        <h2 className="text-2xl font-bold tracking-tight" style={{ color: t.text }}>MTD Pace Trend</h2>
        <p className="text-sm ml-2" style={{ color: t.textMute }}>· Cumulative actual vs target line · {period}</p>
        <div className="ml-auto flex items-center gap-2 text-[10px]" style={{ color: t.textMute }}>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-[2px] bg-[#10B981]"/>Actual</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 border-t border-dashed border-[#6B7280]"/>Target</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 border-t border-dotted border-[#F59E0B]"/>Projected</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 flex-1 min-h-0" style={{ gridAutoRows: 'minmax(0, 1fr)' }}>
        {enriched.length === 0 && (
          <div className="col-span-2 grid place-content-center text-sm" style={{ color: t.textMute }}>
            No targets set for current period. Set targets at Admin &gt; Scorecard &gt; Targets to see pace trend.
          </div>
        )}
        {enriched.map(m => <PaceChart key={m.code} metric={m} t={t} />)}
      </div>
    </div>
  );
}

/* ------------------------- Slide 6: MoM COMPARISON ------------------------- */

function ComparisonView({ currentPace, prevPace, currentPeriod, prevPeriod, metricsMeta, comparisonMetrics, t }) {
  if (!currentPace || !prevPace) {
    return <div className="grid place-content-center flex-1 text-sm" style={{ color: t.textMute }}>Loading comparison data...</div>;
  }

  // Determine current day-of-month cursor from current period pace series
  const currentMetricsRaw = currentPace.metrics || {};
  const prevMetricsRaw = prevPace.metrics || {};

  // Find the latest day with actual data in current period (= today's day of month
  // for the ongoing month, or last day for historical). Fall back to length of series.
  const anyCurrentSeries = Object.values(currentMetricsRaw)[0]?.series || [];
  const cursorDay = (() => {
    const withActual = anyCurrentSeries.filter(p => p.cum_actual !== null && p.cum_actual !== undefined);
    if (withActual.length === 0) return 0;
    return withActual[withActual.length - 1].day || withActual.length;
  })();

  const rows = comparisonMetrics.map(code => {
    const cur = currentMetricsRaw[code];
    const prev = prevMetricsRaw[code];
    const meta = metricsMeta[code] || {};
    if (!cur && !prev) return null;

    const pointAt = (m, day) => {
      if (!m || !m.series) return 0;
      // Find cumulative actual at or before `day`
      const withActual = m.series.filter(p => (p.day || 0) <= day && p.cum_actual != null);
      if (withActual.length === 0) return 0;
      return withActual[withActual.length - 1].cum_actual || 0;
    };

    const curValue = pointAt(cur, cursorDay);
    const prevValue = pointAt(prev, cursorDay);
    const delta = curValue - prevValue;
    const deltaPct = prevValue > 0 ? (delta / prevValue) * 100 : (curValue > 0 ? 100 : 0);
    const target = cur?.target || 0;
    const pct = target > 0 ? (curValue / target) * 100 : 0;

    return {
      code,
      label: meta.label || cur?.name || code,
      prefix: meta.prefix,
      suffix: meta.suffix,
      color: meta.color || '#10B981',
      curValue,
      prevValue,
      delta,
      deltaPct,
      target,
      pct,
    };
  }).filter(Boolean);

  // Header sub-line describing what "same point of month" means
  const [py, pm] = prevPeriod.split('-').map(Number);
  const [cy, cm] = currentPeriod.split('-').map(Number);
  const prevLabel = new Date(py, pm - 1, 1).toLocaleDateString('en-MY', { month: 'short', year: 'numeric' });
  const curLabel = new Date(cy, cm - 1, 1).toLocaleDateString('en-MY', { month: 'short', year: 'numeric' });

  return (
    <div className="flex flex-col gap-2 min-h-0 flex-1">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp size={20} className="text-[#10B981]" />
        <h2 className="text-2xl font-bold tracking-tight" style={{ color: t.text }}>Month-over-Month · Same Day Comparison</h2>
        <p className="text-sm ml-2" style={{ color: t.textMute }}>
          · Day {cursorDay} of {curLabel} MTD vs Day {cursorDay} of {prevLabel} MTD
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2 flex-1 min-h-0" style={{ gridAutoRows: 'minmax(0, 1fr)' }}>
        {rows.length === 0 && (
          <div className="col-span-3 grid place-content-center text-sm" style={{ color: t.textMute }}>
            No comparable metrics. Ensure targets are set for {curLabel} at Admin &gt; Scorecard &gt; Targets.
          </div>
        )}
        {rows.map(r => {
          const positive = r.delta >= 0;
          const trendColor = positive ? '#10B981' : '#E63946';
          const TrendIcon = positive ? TrendingUp : TrendingDown;
          return (
            <div key={r.code} className="rounded-xl p-3 border overflow-hidden relative" style={{ background: t.surface, borderColor: t.border }}>
              <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${r.color}, transparent)` }} />
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.color }} />
                <p className="text-[11px] uppercase tracking-wider font-medium" style={{ color: t.textDim }}>{r.label}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: t.textMute }}>{curLabel} MTD</p>
                  <p className="text-2xl font-bold tabular-nums leading-tight" style={{ color: t.text }}>
                    {r.prefix && <span className="text-sm mr-0.5" style={{ color: t.textDim }}>{r.prefix}</span>}
                    {fmt(r.curValue, { compact: r.curValue >= 10000, decimals: r.curValue < 100 ? 2 : 0 })}
                    {r.suffix && <span className="text-sm" style={{ color: t.textDim }}>{r.suffix}</span>}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: t.textMute }}>{prevLabel} MTD</p>
                  <p className="text-lg font-semibold tabular-nums leading-tight" style={{ color: t.textDim }}>
                    {r.prefix || ''}{fmt(r.prevValue, { compact: r.prevValue >= 10000, decimals: r.prevValue < 100 ? 2 : 0 })}{r.suffix || ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t" style={{ borderColor: t.border }}>
                <span className="flex items-center gap-1 text-sm font-bold tabular-nums" style={{ color: trendColor }}>
                  <TrendIcon size={14} />
                  {positive ? '+' : ''}{r.prefix || ''}{fmt(Math.abs(r.delta), { compact: Math.abs(r.delta) >= 10000, decimals: Math.abs(r.delta) < 100 ? 2 : 0 })}{r.suffix || ''}
                </span>
                <span className="text-sm font-bold tabular-nums" style={{ color: trendColor }}>
                  {positive ? '+' : ''}{r.deltaPct.toFixed(1)}%
                </span>
              </div>
              {r.target > 0 && (
                <div className="mt-1.5 text-[10px] flex items-center justify-between" style={{ color: t.textMute }}>
                  <span>Target {r.prefix || ''}{fmt(r.target, { compact: true })}{r.suffix || ''}</span>
                  <span style={{ color: statusColor(r.pct) }} className="font-bold tabular-nums">{r.pct.toFixed(0)}%</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------- Settings panel ------------------------- */

function SettingsPanel({ open, onClose, settings, setSettings, t }) {
  const update = (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next); saveSettings(next);
  };
  const toggleSlide = (key) => {
    update({ slides: { ...settings.slides, [key]: !settings.slides[key] } });
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-content-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="rounded-2xl border w-[480px] max-w-[92vw] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} style={{ background: t.surface, borderColor: t.border }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings size={18} style={{ color: t.text }} />
            <h3 className="text-base font-bold" style={{ color: t.text }}>TV Wall Settings</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-md" style={{ color: t.textDim }} data-testid="settings-close">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-5">
          {/* Slides toggle */}
          <div>
            <p className="text-xs uppercase tracking-wider mb-2 font-medium" style={{ color: t.textDim }}>Enable slides</p>
            <div className="space-y-1.5">
              {SLIDE_META.map(s => {
                const Icon = s.icon;
                const enabled = settings.slides[s.key];
                return (
                  <button key={s.key} onClick={() => toggleSlide(s.key)}
                    data-testid={`settings-slide-${s.key}`}
                    className="w-full flex items-center justify-between p-2.5 rounded-lg border transition" style={{
                      background: enabled ? '#10B98115' : t.surfaceElev,
                      borderColor: enabled ? '#10B98140' : t.border,
                    }}>
                    <div className="flex items-center gap-2.5">
                      <Icon size={13} style={{ color: enabled ? '#10B981' : t.textMute }} />
                      <span className="text-sm" style={{ color: t.text }}>{s.label}</span>
                    </div>
                    <div className={`relative w-9 h-5 rounded-full transition-all ${enabled ? 'bg-[#10B981]' : ''}`} style={{ background: enabled ? '#10B981' : t.surfaceElev, border: enabled ? 'none' : `1px solid ${t.border}` }}>
                      <div className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: enabled ? '18px' : '2px' }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Duration */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider font-medium" style={{ color: t.textDim }}>Slide duration</p>
              <p className="text-sm font-bold tabular-nums" style={{ color: t.text }}>{settings.duration_sec}s</p>
            </div>
            <input type="range" min="10" max="120" step="5" value={settings.duration_sec}
              data-testid="settings-duration"
              onChange={(e) => update({ duration_sec: parseInt(e.target.value) })}
              className="w-full accent-[#10B981]" />
            <div className="flex justify-between text-[10px] mt-1" style={{ color: t.textMute }}>
              <span>10s</span><span>60s</span><span>120s</span>
            </div>
          </div>

          {/* Theme */}
          <div>
            <p className="text-xs uppercase tracking-wider mb-2 font-medium" style={{ color: t.textDim }}>Theme</p>
            <div className="grid grid-cols-2 gap-2">
              {[{ k: 'dark', l: 'Dark', icon: Moon }, { k: 'light', l: 'Light', icon: Sun }].map(opt => {
                const Icon = opt.icon;
                const sel = settings.theme === opt.k;
                return (
                  <button key={opt.k} onClick={() => update({ theme: opt.k })}
                    data-testid={`settings-theme-${opt.k}`}
                    className="p-3 rounded-lg border transition flex items-center justify-center gap-2 text-sm font-medium" style={{
                      background: sel ? '#10B98115' : t.surfaceElev,
                      borderColor: sel ? '#10B98140' : t.border,
                      color: sel ? '#10B981' : t.text,
                    }}>
                    <Icon size={14} />{opt.l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Pace metrics — multi-select */}
          <div>
            <p className="text-xs uppercase tracking-wider mb-2 font-medium" style={{ color: t.textDim }}>Pace Trend metrics</p>
            <div className="grid grid-cols-4 gap-1.5">
              {['REV','LEAD','CLS','LOT','SUB','ACA','PIP','VIA','VID','POS','IBH','SIGC'].map(code => {
                const sel = (settings.pace_metrics || []).includes(code);
                return (
                  <button key={code} onClick={() => {
                    const curr = settings.pace_metrics || [];
                    const next = sel ? curr.filter(c => c !== code) : [...curr, code];
                    update({ pace_metrics: next });
                  }}
                  data-testid={`settings-pace-${code}`}
                  className="px-2 py-1.5 rounded text-[10px] font-bold transition" style={{
                    background: sel ? '#3B82F615' : t.surfaceElev,
                    borderColor: sel ? '#3B82F640' : t.border,
                    border: '1px solid',
                    color: sel ? '#3B82F6' : t.textMute,
                  }}>{code}</button>
                );
              })}
            </div>
            <p className="text-[9px] mt-1.5" style={{ color: t.textMute }}>Pick up to 6 untuk grid 3×2. Default REV, LEAD, CLS, LOT.</p>
          </div>

          {/* MoM Comparison metrics — multi-select */}
          <div>
            <p className="text-xs uppercase tracking-wider mb-2 font-medium" style={{ color: t.textDim }}>MoM Comparison metrics</p>
            <div className="grid grid-cols-4 gap-1.5">
              {['REV','LEAD','CLS','LOT','SUB','ACA','PIP','VIA','VID','POS','IBH','SIGC'].map(code => {
                const sel = (settings.comparison_metrics || []).includes(code);
                return (
                  <button key={code} onClick={() => {
                    const curr = settings.comparison_metrics || [];
                    const next = sel ? curr.filter(c => c !== code) : [...curr, code];
                    update({ comparison_metrics: next });
                  }}
                  data-testid={`settings-cmp-${code}`}
                  className="px-2 py-1.5 rounded text-[10px] font-bold transition" style={{
                    background: sel ? '#10B98115' : t.surfaceElev,
                    borderColor: sel ? '#10B98140' : t.border,
                    border: '1px solid',
                    color: sel ? '#10B981' : t.textMute,
                  }}>{code}</button>
                );
              })}
            </div>
            <p className="text-[9px] mt-1.5" style={{ color: t.textMute }}>Metrics untuk slide MoM Comparison (bulan ini vs bulan lepas, hari sama).</p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-5 pt-4 border-t" style={{ borderColor: t.border }}>
          <button onClick={() => { setSettings(DEFAULT_SETTINGS); saveSettings(DEFAULT_SETTINGS); }}
            data-testid="settings-reset"
            className="text-[10px] uppercase tracking-wider hover:underline" style={{ color: t.textMute }}>Reset to defaults</button>
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm font-medium bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white">Done</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------- Main page ------------------------- */

export default function TvWallPage() {
  const [settings, setSettings] = useState(loadSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const [paused, setPaused] = useState(() => new URLSearchParams(window.location.search).get('paused') === '1');
  const [paceData, setPaceData] = useState(null);
  const [prevPaceData, setPrevPaceData] = useState(null);

  // Period picker — default current month, allow browsing last 6
  const currentYYYYMM = new Date().toISOString().slice(0, 7);
  const [selectedPeriod, setSelectedPeriod] = useState(currentYYYYMM);
  const isViewingCurrent = selectedPeriod === currentYYYYMM;

  const { data, metrics, channels, daysRemaining, weekdaysRemaining, lastUpdated } = useTvWallData(selectedPeriod);
  const t = useMemo(() => ({ ...THEME[settings.theme || 'dark'], theme: settings.theme || 'dark' }), [settings.theme]);

  // Build period picker options: next month + last 12 months
  const periodOptions = useMemo(() => {
    const now = new Date();
    const opts = [];
    for (let i = -1; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      let label = d.toLocaleDateString('en-MY', { month: 'short', year: 'numeric' });
      if (i === -1) label = `${label} (Next)`;
      else if (i === 0) label = `${label} (Current)`;
      opts.push({ value: v, label });
    }
    return opts;
  }, []);

  // Active slide keys based on enabled
  const activeSlides = useMemo(() => SLIDE_META.filter(s => settings.slides[s.key]), [settings.slides]);
  const currentSlide = activeSlides[slideIdx % Math.max(activeSlides.length, 1)] || activeSlides[0];

  // Fetch pace data when pace or comparison slide enabled (refresh every 5min)
  useEffect(() => {
    if (!(settings.slides.pace || settings.slides.comparison) || !data) return;
    const fetchPace = async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/scorecard/mtd-pace?period=${data.period}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setPaceData(r.data);
      } catch { /* noop */ }
    };
    fetchPace();
    const id = setInterval(fetchPace, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [settings.slides.pace, settings.slides.comparison, data]);

  // Fetch previous month pace data for MoM comparison slide
  useEffect(() => {
    if (!settings.slides.comparison || !data?.period) return;
    const [py, pm] = data.period.split('-').map(Number);
    const prevDate = new Date(py, pm - 2, 1); // one month before selected
    const prevPeriod = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
    const fetchPrev = async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/scorecard/mtd-pace?period=${prevPeriod}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setPrevPaceData({ ...r.data, period: prevPeriod });
      } catch { /* noop */ }
    };
    fetchPrev();
    const id = setInterval(fetchPrev, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [settings.slides.comparison, data]);

  useEffect(() => {
    if (paused || !data || activeSlides.length <= 1) return;
    const id = setInterval(() => setSlideIdx(s => (s + 1) % activeSlides.length), (settings.duration_sec || 30) * 1000);
    return () => clearInterval(id);
  }, [paused, data, activeSlides.length, settings.duration_sec]);

  useEffect(() => { if (slideIdx >= activeSlides.length && activeSlides.length > 0) setSlideIdx(0); }, [activeSlides, slideIdx]);

  if (!data) {
    return (
      <div className="min-h-screen grid place-content-center" style={{ background: t.bg }}>
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-[#10B981] animate-pulse" />
          <span className="uppercase tracking-widest text-xs" style={{ color: t.textDim }}>Loading TV Wall...</span>
        </div>
      </div>
    );
  }

  const totalDaysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
  const dayProgress = ((totalDaysInMonth - daysRemaining) / totalDaysInMonth) * 100;

  return (
    <div className="min-h-screen p-5 flex flex-col" style={{ aspectRatio: '16/9', maxWidth: 1920, margin: '0 auto', background: t.bg, color: t.text }}>
      {/* HEADER */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#10B981] to-[#3B82F6] flex items-center justify-center shadow-[0_4px_20px_rgba(16,185,129,0.4)]">
            <Activity size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight" style={{ color: t.text }}>Live KPI Wall · Underads Capital</h1>
            <p className="text-[10px]" style={{ color: t.textMute }}>{currentSlide?.label || '—'} · {data.period} MTD · {daysRemaining} days left · auto-rotate {settings.duration_sec}s</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          {/* Period picker */}
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            data-testid="tv-period-select"
            className="text-[11px] rounded-md px-2 py-1 border cursor-pointer focus:outline-none"
            style={{ background: t.surfaceElev, color: t.text, borderColor: t.border }}
            title="Pilih bulan untuk lihat data"
          >
            {periodOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          {!isViewingCurrent && (
            <button
              onClick={() => setSelectedPeriod(currentYYYYMM)}
              data-testid="tv-period-reset"
              className="text-[10px] px-2 py-1 rounded-md border"
              style={{ color: '#10B981', borderColor: '#10B98140', background: '#10B98115' }}
              title="Balik ke bulan semasa"
            >Today</button>
          )}
          {activeSlides.length > 1 && (
            <>
              <button onClick={() => setSlideIdx((slideIdx - 1 + activeSlides.length) % activeSlides.length)}
                data-testid="tv-prev-btn" className="p-1.5 hover:opacity-70 rounded-md" style={{ color: t.textDim }}><ChevronLeft size={14} /></button>
              <button onClick={() => setPaused(p => !p)} data-testid="tv-pause-btn"
                className="p-1.5 rounded-md" style={{ background: paused ? '#F59E0B22' : 'transparent', color: paused ? '#F59E0B' : t.textDim }}>
                {paused ? <Play size={14} /> : <Pause size={14} />}
              </button>
              <button onClick={() => setSlideIdx((slideIdx + 1) % activeSlides.length)}
                data-testid="tv-next-btn" className="p-1.5 hover:opacity-70 rounded-md" style={{ color: t.textDim }}><ChevronRight size={14} /></button>
            </>
          )}
          <button onClick={() => setSettingsOpen(true)} data-testid="tv-settings-btn"
            className="p-1.5 hover:opacity-70 rounded-md ml-1" style={{ color: t.textDim }}><Settings size={14} /></button>
          <button onClick={() => { const next = { ...settings, theme: t.theme === 'dark' ? 'light' : 'dark' }; setSettings(next); saveSettings(next); }}
            data-testid="tv-theme-btn" className="p-1.5 hover:opacity-70 rounded-md" style={{ color: t.textDim }}>
            {t.theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse ml-2" />
          <span className="text-[#10B981] font-bold">LIVE</span>
          <span className="tabular-nums" style={{ color: t.textDim }}>{new Date().toLocaleString('en-MY', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      </div>

      {/* MAIN — Full width (sidebar removed per user preference) */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex flex-col min-h-0 flex-1 transition-opacity duration-500" key={currentSlide?.key}>
          {currentSlide?.key === 'overall'  && <OverallView data={data} metrics={metrics} t={t} />}
          {currentSlide?.key === 'channels' && <ChannelsView channels={channels} t={t} />}
          {currentSlide?.key === 'chart'    && <ChartView metrics={metrics} t={t} />}
          {currentSlide?.key === 'tracker'  && <TrackerView metrics={metrics} weekdaysRemaining={weekdaysRemaining} t={t} />}
          {currentSlide?.key === 'pace'     && <PaceView paceData={paceData} paceMetrics={settings.pace_metrics || ['REV','LEAD','CLS','LOT']}
            metricsMeta={Object.fromEntries(metrics.map(m => [m.code, m]))} period={data.period} t={t} />}
          {currentSlide?.key === 'comparison' && <ComparisonView
            currentPace={paceData}
            prevPace={prevPaceData}
            currentPeriod={data.period}
            prevPeriod={prevPaceData?.period || ''}
            metricsMeta={Object.fromEntries(metrics.map(m => [m.code, m]))}
            comparisonMetrics={settings.comparison_metrics || ['REV','LEAD','CLS','LOT','SUB','ACA']}
            t={t}
          />}
          {!currentSlide && (
            <div className="grid place-content-center flex-1 text-sm" style={{ color: t.textMute }}>
              Tiada slide aktif. Tekan ⚙ Settings untuk enable.
            </div>
          )}
        </div>
      </div>

      {/* FOOTER */}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-1.5">
          {activeSlides.map((s, i) => (
            <button key={s.key} onClick={() => setSlideIdx(i)} data-testid={`tv-slide-dot-${i}`}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === slideIdx ? '40px' : '28px',
                background: i === slideIdx ? 'linear-gradient(90deg, #10B981, #3B82F6)' : t.surfaceElev,
              }} />
          ))}
          {activeSlides.length > 0 && (
            <span className="text-[10px] ml-2" style={{ color: t.textMute }}>
              {slideIdx + 1}/{activeSlides.length} · {currentSlide?.label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px]" style={{ color: t.textMute }}>
          <span className="tabular-nums">updated {lastUpdated?.toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })}</span>
          {paused && <span className="ml-2 px-2 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] font-bold rounded-full text-[9px]">PAUSED</span>}
        </div>
      </div>

      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} settings={settings} setSettings={setSettings} t={t} />
    </div>
  );
}
